// Unique Identifier Code (UIC) Generation Service
// Following the mobile app logic

/**
 * Generates Unique Identifier Code (UIC) based on parent information and user details
 * Format: [MOTHER_CODE][FATHER_CODE][BIRTH_ORDER][MMDDYYYY]
 * 
 * @param {Object} params - Parameters for UIC generation
 * @param {string} params.motherFirstName - Mother's first name (optional)
 * @param {string} params.fatherFirstName - Father's first name (optional)
 * @param {string} params.userFirstName - User's first name (required)
 * @param {string} params.userLastName - User's last name (required)
 * @param {string} params.birthOrder - Birth order (e.g., "01", "02")
 * @param {string} params.dateOfBirth - Date of birth (YYYY-MM-DD format)
 * @returns {string} Generated UIC
 */
export const generateUIC = ({
  motherFirstName,
  fatherFirstName,
  userFirstName,
  userLastName,
  birthOrder,
  dateOfBirth
}) => {
  // Helper functions
  const isUnknown = (value) => {
    return !value || value.trim() === '' || value.trim() === 'null' || value.trim() === 'undefined';
  };

  const firstTwo = (str) => {
    const clean = str.trim().toUpperCase();
    return clean.length >= 2 ? clean.substring(0, 2) : clean.padEnd(2, 'X');
  };

  // Generate user initials
  const userInitials = firstTwo(userFirstName.charAt(0) + userLastName.charAt(0));

  // Determine parent codes based on availability
  let motherCode, fatherCode;
  
  if (isUnknown(motherFirstName) && isUnknown(fatherFirstName)) {
    // Both parents unknown
    motherCode = userInitials;
    fatherCode = userInitials;
  } else if (isUnknown(motherFirstName)) {
    // Only mother unknown
    motherCode = userInitials;
    fatherCode = firstTwo(fatherFirstName);
  } else if (isUnknown(fatherFirstName)) {
    // Only father unknown
    motherCode = firstTwo(motherFirstName);
    fatherCode = userInitials;
  } else {
    // Both parents known
    motherCode = firstTwo(motherFirstName);
    fatherCode = firstTwo(fatherFirstName);
  }

  // Format birth order (ensure 2 digits)
  const birthOrderSegment = String(birthOrder).padStart(2, '0');

  // Format DOB to MMDDYYYY (4-digit year)
  let dobSegment = '';
  if (dateOfBirth) {
    try {
      const date = new Date(dateOfBirth);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      dobSegment = `${month}${day}${year}`;
    } catch (error) {
      console.error('Invalid date format for UIC generation:', dateOfBirth);
      dobSegment = '01011950'; // Default fallback (4-digit year)
    }
  }

  // Combine all segments
  const code = `${motherCode}${fatherCode}${birthOrderSegment}${dobSegment}`;
  
  return code;
};

/**
 * Stores generated UIC in localStorage
 * @param {string} uic - The generated UIC
 */
export const storeUIC = (uic) => {
  try {
    localStorage.setItem('generatedUIC', uic);
  } catch (error) {
    console.error('Failed to store UIC in localStorage:', error);
  }
};

/**
 * Retrieves stored UIC from localStorage
 * @returns {string|null} The stored UIC or null if not found
 */
export const getStoredUIC = () => {
  try {
    return localStorage.getItem('generatedUIC');
  } catch (error) {
    console.error('Failed to retrieve UIC from localStorage:', error);
    return null;
  }
};

/**
 * Clears stored UIC from localStorage
 */
export const clearStoredUIC = () => {
  try {
    localStorage.removeItem('generatedUIC');
  } catch (error) {
    console.error('Failed to clear UIC from localStorage:', error);
  }
};
